import { useActorRef, useSelector } from '@xstate/react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router';
import type { SnapshotFrom } from 'xstate';
import { UserContext } from '~/app';
import { Button } from '~/components/buttons';
import { isNotificationArrival } from '~/features/notifications/constants';
import { useDeepLinkedResponse } from '~/features/shared/deepLinkParams';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketResponseListItem,
  MarketWorkspaceGrid,
  formatResponseStatusLabel,
} from '~/features/shared/market-content';
import { selectUnmaskViewerScope } from '~/features/shared/market-content/ResponseUnmaskPanel';
import { OpportunitiesSidebar } from '~/features/shared/OpportunitiesSidebar';
import { buildProviderViewUrl } from '~/routing/urls';
import { ServiceBeginFlow } from './ServiceBeginFlow';
import { ServiceConversation } from './ServiceConversation';
import { serviceOpportunitiesMachine } from './serviceOpportunities.machine';
import { ServiceResponseBoundary } from './ServiceResponseBoundary';
import { useRefreshOnReturn } from './useRefreshOnReturn';

type Snapshot = SnapshotFrom<typeof serviceOpportunitiesMachine>;
const selectData = (s: Snapshot) => s.context.data;
const selectFilter = (s: Snapshot) => s.context.filter;
const selectIncludeDeclined = (s: Snapshot) => s.context.includeDeclined;
const selectLoading = (s: Snapshot) => s.matches('loading');
const selectError = (s: Snapshot) => s.context.error;
const selectUserInfoId = (s: { context: { userInfoId?: number } }) => s.context.userInfoId;

type Copy = {
  title: string;
  count: (total: number) => string;
  truncated: (shown: number, total: number) => string;
  unavailable: string;
  empty: string;
  select: string;
  invalid: string;
  noneSelected: string;
  /** Replaces the machine's load error, which carries the provider wording. */
  loadError?: string;
};
// Employers see these as provider responses and providers as service responses,
// matching their nav labels and Job Responses.
const EMPLOYER_COPY: Copy = {
  title: 'Provider Responses',
  count: (total) => `${total} ${total === 1 ? 'response' : 'responses'}`,
  truncated: (shown, total) =>
    `Showing ${shown} of ${total} responses. Use the filters to narrow the list.`,
  unavailable: 'Responses unavailable',
  empty: 'No responses',
  select: 'Select a response',
  invalid: 'Invalid response link',
  noneSelected: 'No response selected',
  loadError: 'Unable to load responses.',
};
const PROVIDER_COPY: Copy = {
  title: 'Service Responses',
  count: (total) => `${total} ${total === 1 ? 'response' : 'responses'}`,
  truncated: (shown, total) =>
    `Showing ${shown} of ${total} responses. Use the filters to narrow the list.`,
  unavailable: 'Responses unavailable',
  empty: 'No service responses',
  select: 'Select a service response',
  invalid: 'Invalid response link',
  noneSelected: 'No response selected',
};

export const ServiceOpportunities = () => {
  const user = UserContext.useActorRef();
  const viewerScopeKey = useSelector(user, selectUnmaskViewerScope);
  const currentUserInfoId = useSelector(user, selectUserInfoId);
  const isProvider = useSelector(user, (state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  return (
    <ServiceResponseBoundary viewerScopeKey={viewerScopeKey}>
      <ScopedOpportunities
        key={viewerScopeKey}
        viewerScopeKey={viewerScopeKey}
        currentUserInfoId={currentUserInfoId}
        copy={isProvider ? PROVIDER_COPY : EMPLOYER_COPY}
      />
    </ServiceResponseBoundary>
  );
};

const ScopedOpportunities = ({
  viewerScopeKey,
  currentUserInfoId,
  copy,
}: {
  viewerScopeKey: string;
  currentUserInfoId?: number;
  copy: Copy;
}) => {
  const { responseId } = useDeepLinkedResponse();
  const actor = useActorRef(serviceOpportunitiesMachine, {
    input: { focusResponseId: responseId },
  });
  const data = useSelector(actor, selectData);
  const filter = useSelector(actor, selectFilter);
  const includeDeclined = useSelector(actor, selectIncludeDeclined);
  const loading = useSelector(actor, selectLoading);
  const error = useSelector(actor, selectError);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const invalidLink = params.has('responseId') && responseId === undefined;
  const selectedId = invalidLink ? undefined : (responseId ?? data?.data[0]?.responseId);
  const refresh = () => actor.send({ type: 'REFRESH' });
  // [MCV-1753] A notification click on the response already open changes neither `responseId` nor
  // the path, and useMarketPageSync leaves this page to deep-link itself, so nothing re-read the
  // inbox or the open conversation. The key of that click counts a refresh. It is latched, as in
  // useContractsService: it only moves for a NEW click (never on mount, where the page loads fresh
  // anyway, and never on a POP), so a plain navigation does not re-read anything.
  const location = useLocation();
  const navigationType = useNavigationType();
  const arrivalKey = isNotificationArrival(location.state, navigationType) ? location.key : null;
  const [seenArrivalKey, setSeenArrivalKey] = useState(arrivalKey);
  const [notificationRefreshes, setNotificationRefreshes] = useState(0);
  if (arrivalKey !== null && arrivalKey !== seenArrivalKey) {
    setSeenArrivalKey(arrivalKey);
    setNotificationRefreshes((count) => count + 1);
  }
  useEffect(() => {
    if (notificationRefreshes > 0) actor.send({ type: 'REFRESH' });
  }, [actor, notificationRefreshes]);
  // No manual refresh button (MCV-1698), so re-read the inbox whenever the tab
  // regains focus, as the open response already does.
  useRefreshOnReturn(refresh);
  useEffect(() => {
    if (!params.has('responseId') && selectedId != null) {
      const next = new URLSearchParams(params);
      next.set('responseId', String(selectedId));
      setParams(next, { replace: true });
    }
  }, [params, selectedId, setParams]);
  const sidebar = (
    <OpportunitiesSidebar
      opportunities={data?.data ?? []}
      selectedResponseId={selectedId}
      filter={filter}
      includeDeclined={includeDeclined}
      loading={loading}
      title={copy.title}
      subtitle={
        data == null
          ? undefined
          : data.truncated
            ? copy.truncated(data.data.length, data.total)
            : copy.count(data.total)
      }
      emptyState={{
        title: error ? copy.unavailable : copy.empty,
        description: error
          ? (copy.loadError ?? error)
          : 'Sent and received service inquiries appear here.',
        action: error ? (
          <Button
            type="button"
            size="sm"
            colorVariant="outline"
            label="Try again"
            onClick={refresh}
            cyTag="service-inbox-retry"
          />
        ) : undefined,
      }}
      onFilter={(next) => actor.send({ type: 'FILTER', filter: next })}
      onIncludeDeclined={(next) => actor.send({ type: 'INCLUDE_DECLINED', includeDeclined: next })}
      onSelect={(id) => {
        const next = new URLSearchParams(params);
        next.set('responseId', String(id));
        setParams(next);
      }}
      renderOpportunity={(row, selected, select) => (
        <MarketResponseListItem
          selected={selected}
          title={row.listing.title}
          subtitle={row.counterparty.displayName}
          status={row.status === 'ACCEPTED' ? 'accepted' : 'responded'}
          statusLabel={
            row.status === 'MESSAGED' ? 'Inquiry' : formatResponseStatusLabel(row.status)
          }
          meta={row.viewerRole === 'RESPONDER' ? 'Sent inquiry' : 'Received inquiry'}
          aside={row.unreadCount > 0 ? `${row.unreadCount} unread` : undefined}
          dataCy={`service-response-${row.responseId}`}
          onClick={() => select(row.responseId)}
        />
      )}
    />
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col p-4 min-[1360px]:p-6">
      {selectedId != null ? (
        <ServiceBeginFlow key={`${viewerScopeKey}:${selectedId}`} responseId={selectedId}>
          {({ open, revision, busy }) => (
            <ServiceConversation
              responseId={selectedId}
              viewerScopeKey={viewerScopeKey}
              currentUserInfoId={currentUserInfoId}
              sidebar={sidebar}
              onChanged={refresh}
              onBeginNegotiation={open}
              // Both counters only ever grow, so the sum changes for either one and the open
              // thread re-reads in place, without a remount that would drop an unsent reply.
              refreshKey={revision + notificationRefreshes}
              progressionBusy={busy}
              onViewProvider={(providerId, id) =>
                navigate(buildProviderViewUrl({ providerId, responseId: id }))
              }
            />
          )}
        </ServiceBeginFlow>
      ) : (
        <MarketWorkspaceGrid
          dataCy="service-opportunities-grid"
          sidebar={sidebar}
          detail={
            <MarketContentPanel className="h-full" variant="empty">
              <MarketEmptyPanel title={invalidLink ? copy.invalid : copy.select} />
            </MarketContentPanel>
          }
          conversation={
            <MarketContentPanel className="h-full" variant="empty">
              <MarketEmptyPanel title={copy.noneSelected} />
            </MarketContentPanel>
          }
        />
      )}
    </div>
  );
};
